import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Alert, Modal } from 'react-bootstrap';
import { FaUser, FaEdit, FaSave, FaTimes, FaKey, FaUnlock } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import Spinner from '../components/Spinner';
import * as api from '../services/api';
import './UserProfile.css';

const UserProfile = () => {
  const { currentUser } = useAuth();
  const [profileData, setProfileData] = useState({
    username: '',
    email: '',
    given_name: '',
    family_name: '',
    phone_number: ''
  });
  
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Forgot password modal state
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState('request'); // 'request' or 'confirm'
  const [forgotPasswordData, setForgotPasswordData] = useState({
    username: '',
    confirmationCode: '',
    newPassword: '',
    confirmNewPassword: ''
  });
  const [forgotPasswordError, setForgotPasswordError] = useState('');
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState('');
  const [isForgotPasswordSubmitting, setIsForgotPasswordSubmitting] = useState(false);

  // Helper to extract error message from API response
  const extractErrorMessage = (response) => {
    if (!response) return 'An unknown error occurred.';
    
    if (response.error && typeof response.error === 'string') {
      return response.error;
    }
    
    if (response.error && typeof response.error === 'object') {
      return response.error.message || JSON.stringify(response.error);
    }
    
    return 'An unexpected error occurred. Please try again.';
  };

  // Load user profile data from backend on mount
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        if (!currentUser) {
          setIsLoading(false);
          return;
        }

        console.log('Loading user profile from backend API...');
        const response = await api.getUserProfile();
        
        console.log('User profile response:', response);

        if (response && response.success && response.data) {
          const userData = response.data;
          setProfileData({
            username: userData.username || '',
            email: userData.email || '',
            given_name: userData.given_name || '',
            family_name: userData.family_name || '',
            phone_number: userData.phone_number || ''
          });
        } else {
          throw new Error(extractErrorMessage(response));
        }
        
        setIsLoading(false);
      } catch (err) {
        console.error('Error loading user profile:', err);
        setError('Failed to load user profile data. Please refresh the page.');
        setIsLoading(false);
      }
    };

    loadUserProfile();
  }, [currentUser]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditToggle = () => {
    if (isEditing) {
      setIsEditing(false);
      // Reload profile data to revert changes
      loadUserProfile();
    } else {
      setIsEditing(true);
    }
    
    setError(null);
    setSuccessMessage('');
  };

  // Helper function to reload user profile
  const loadUserProfile = async () => {
    try {
      setIsLoading(true);
      const response = await api.getUserProfile();
      
      if (response && response.success && response.data) {
        const userData = response.data;
        setProfileData({
          username: userData.username || '',
          email: userData.email || '',
          given_name: userData.given_name || '',
          family_name: userData.family_name || '',
          phone_number: userData.phone_number || ''
        });
      }
      
      setIsLoading(false);
    } catch (err) {
      console.error('Error reloading user profile:', err);
      setError('Failed to reload user profile data.');
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);
    setSuccessMessage('');
    
    try {
      // Prepare attributes to update (only non-empty fields)
      const attributesToUpdate = {};
      
      if (profileData.given_name) {
        attributesToUpdate.first_name = profileData.given_name;
      }
      if (profileData.family_name) {
        attributesToUpdate.last_name = profileData.family_name;
      }
      if (profileData.phone_number) {
        attributesToUpdate.phone_number = profileData.phone_number;
      }
      if (profileData.email) {
        attributesToUpdate.email = profileData.email;
      }
      
      console.log('Updating user attributes:', attributesToUpdate);
      const response = await api.updateUserAttributes(attributesToUpdate);
      
      console.log('Update response:', response);

      if (response && response.success) {
        setSuccessMessage('Profile updated successfully!');
        setIsEditing(false);
        // Reload to get fresh data from backend
        await loadUserProfile();
      } else {
        throw new Error(extractErrorMessage(response));
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile: ' + (err.message || 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    
    // Validate passwords
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    if (passwordData.newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters long');
      return;
    }
    
    setIsSaving(true);
    
    try {
      console.log('Changing password...');
      const response = await api.changeUserPassword(
        passwordData.oldPassword,
        passwordData.newPassword
      );
      
      console.log('Change password response:', response);

      if (response && response.success) {
        setPasswordSuccess('Password updated successfully!');
        setPasswordData({
          oldPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        throw new Error(extractErrorMessage(response));
      }
    } catch (err) {
      console.error('Error updating password:', err);
      setPasswordError('Failed to update password: ' + (err.message || 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };

  // Forgot password handlers
  const handleForgotPasswordOpen = () => {
    setShowForgotPasswordModal(true);
    setForgotPasswordStep('request');
    setForgotPasswordData({
      username: profileData.email || '',
      confirmationCode: '',
      newPassword: '',
      confirmNewPassword: ''
    });
    setForgotPasswordError('');
    setForgotPasswordSuccess('');
  };

  const handleForgotPasswordClose = () => {
    setShowForgotPasswordModal(false);
    setForgotPasswordStep('request');
    setForgotPasswordData({
      username: '',
      confirmationCode: '',
      newPassword: '',
      confirmNewPassword: ''
    });
    setForgotPasswordError('');
    setForgotPasswordSuccess('');
  };

  const handleForgotPasswordChange = (e) => {
    const { name, value } = e.target;
    setForgotPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleForgotPasswordRequest = async (e) => {
    e.preventDefault();
    setForgotPasswordError('');
    setForgotPasswordSuccess('');
    
    if (!forgotPasswordData.username) {
      setForgotPasswordError('Please enter your email address');
      return;
    }
    
    setIsForgotPasswordSubmitting(true);
    
    try {
      console.log('Requesting password reset for:', forgotPasswordData.username);
      const response = await api.resetUserPassword(forgotPasswordData.username);
      
      console.log('Reset password response:', response);

      if (response && response.success) {
        setForgotPasswordSuccess('A verification code has been sent to your email.');
        setForgotPasswordStep('confirm');
      } else {
        throw new Error(extractErrorMessage(response));
      }
    } catch (err) {
      console.error('Error requesting password reset:', err);
      setForgotPasswordError('Failed to request password reset: ' + (err.message || 'Unknown error'));
    } finally {
      setIsForgotPasswordSubmitting(false);
    }
  };

  const handleForgotPasswordConfirm = async (e) => {
    e.preventDefault();
    setForgotPasswordError('');
    setForgotPasswordSuccess('');
    
    // Validate inputs
    if (!forgotPasswordData.confirmationCode) {
      setForgotPasswordError('Please enter the verification code');
      return;
    }
    
    if (!forgotPasswordData.newPassword) {
      setForgotPasswordError('Please enter a new password');
      return;
    }
    
    if (forgotPasswordData.newPassword !== forgotPasswordData.confirmNewPassword) {
      setForgotPasswordError('Passwords do not match');
      return;
    }
    
    if (forgotPasswordData.newPassword.length < 8) {
      setForgotPasswordError('Password must be at least 8 characters long');
      return;
    }
    
    setIsForgotPasswordSubmitting(true);
    
    try {
      console.log('Confirming password reset...');
      const response = await api.confirmResetUserPassword(
        forgotPasswordData.username,
        forgotPasswordData.confirmationCode,
        forgotPasswordData.newPassword
      );
      
      console.log('Confirm reset response:', response);

      if (response && response.success) {
        setForgotPasswordSuccess('Password reset successfully! You can now close this window.');
        setTimeout(() => {
          handleForgotPasswordClose();
        }, 2000);
      } else {
        throw new Error(extractErrorMessage(response));
      }
    } catch (err) {
      console.error('Error confirming password reset:', err);
      setForgotPasswordError('Failed to reset password: ' + (err.message || 'Unknown error'));
    } finally {
      setIsForgotPasswordSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Container className="profile-container">
        <div className="text-center py-5">
          <Spinner />
          <p className="mt-2">Loading profile data...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container className="profile-container">
      <Row className="justify-content-center">
        <Col lg={8}>
          <Card className="profile-card mb-4">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="d-flex align-items-center">
                  <div className="profile-avatar">
                    <FaUser size={24} />
                  </div>
                  <div className="ms-3">
                    <h2 className="profile-title">User Profile</h2>
                    <p className="text-muted mb-0">Manage your account information</p>
                  </div>
                </div>
                <Button 
                  variant={isEditing ? "outline-secondary" : "outline-primary"}
                  onClick={handleEditToggle}
                >
                  {isEditing ? (
                    <>
                      <FaTimes className="me-2" /> Cancel
                    </>
                  ) : (
                    <>
                      <FaEdit className="me-2" /> Edit Profile
                    </>
                  )}
                </Button>
              </div>

              {error && <Alert variant="danger">{error}</Alert>}
              {successMessage && <Alert variant="success">{successMessage}</Alert>}

              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>First Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="given_name"
                        value={profileData.given_name}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Last Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="family_name"
                        value={profileData.family_name}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Email Address</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={profileData.email}
                    onChange={handleInputChange}
                    disabled={true}
                  />
                  <Form.Text className="text-muted">
                    Email cannot be changed through this interface
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Phone Number</Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone_number"
                    value={profileData.phone_number}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder="+12065551234"
                  />
                  <Form.Text className="text-muted">
                    Enter in E.164 format (e.g., +12065551234)
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Username</Form.Label>
                  <Form.Control
                    type="text"
                    value={profileData.username}
                    disabled={true}
                  />
                  <Form.Text className="text-muted">
                    Username cannot be changed
                  </Form.Text>
                </Form.Group>

                {isEditing && (
                  <div className="d-grid gap-2">
                    <Button 
                      variant="primary" 
                      type="submit"
                      disabled={isSaving}
                    >
                      {isSaving ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <FaSave className="me-2" />
                          Save Changes
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </Form>
            </Card.Body>
          </Card>

          <Card className="profile-card mb-4">
            <Card.Body>
              <div className="d-flex align-items-center mb-4">
                <div className="profile-avatar">
                  <FaKey size={24} />
                </div>
                <div className="ms-3">
                  <h3 className="profile-title">Change Password</h3>
                  <p className="text-muted mb-0">Update your password</p>
                </div>
              </div>

              {passwordError && <Alert variant="danger">{passwordError}</Alert>}
              {passwordSuccess && <Alert variant="success">{passwordSuccess}</Alert>}

              <Form onSubmit={handlePasswordSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Current Password</Form.Label>
                  <Form.Control
                    type="password"
                    name="oldPassword"
                    value={passwordData.oldPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>New Password</Form.Label>
                  <Form.Control
                    type="password"
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                  <Form.Text className="text-muted">
                    Password must be at least 8 characters long
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Confirm New Password</Form.Label>
                  <Form.Control
                    type="password"
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                </Form.Group>

                <div className="d-grid gap-2">
                  <Button 
                    variant="primary" 
                    type="submit"
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Updating Password...
                      </>
                    ) : (
                      <>
                        <FaKey className="me-2" />
                        Change Password
                      </>
                    )}
                  </Button>
                </div>
              </Form>

              <hr className="my-4" />

              <div className="text-center">
                <Button 
                  variant="link" 
                  onClick={handleForgotPasswordOpen}
                  className="forgot-password-link"
                >
                  <FaUnlock className="me-2" />
                  Forgot Password?
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Forgot Password Modal */}
      <Modal 
        show={showForgotPasswordModal} 
        onHide={handleForgotPasswordClose}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaUnlock className="me-2" />
            Reset Password
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {forgotPasswordError && (
            <Alert variant="danger" dismissible onClose={() => setForgotPasswordError('')}>
              {forgotPasswordError}
            </Alert>
          )}
          {forgotPasswordSuccess && (
            <Alert variant="success">
              {forgotPasswordSuccess}
            </Alert>
          )}

          {forgotPasswordStep === 'request' && (
            <Form onSubmit={handleForgotPasswordRequest}>
              <Form.Group className="mb-3">
                <Form.Label>Email Address</Form.Label>
                <Form.Control
                  type="email"
                  name="username"
                  value={forgotPasswordData.username}
                  onChange={handleForgotPasswordChange}
                  placeholder="Enter your email address"
                  required
                />
                <Form.Text className="text-muted">
                  Enter the email address associated with your account. We'll send you a verification code.
                </Form.Text>
              </Form.Group>

              <div className="d-grid gap-2">
                <Button 
                  variant="primary" 
                  type="submit"
                  disabled={isForgotPasswordSubmitting}
                >
                  {isForgotPasswordSubmitting ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Sending Code...
                    </>
                  ) : (
                    'Send Verification Code'
                  )}
                </Button>
                <Button 
                  variant="secondary" 
                  onClick={handleForgotPasswordClose}
                  disabled={isForgotPasswordSubmitting}
                >
                  Cancel
                </Button>
              </div>
            </Form>
          )}

          {forgotPasswordStep === 'confirm' && (
            <Form onSubmit={handleForgotPasswordConfirm}>
              <Form.Group className="mb-3">
                <Form.Label>Email Address</Form.Label>
                <Form.Control
                  type="email"
                  name="username"
                  value={forgotPasswordData.username}
                  onChange={handleForgotPasswordChange}
                  disabled
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Verification Code</Form.Label>
                <Form.Control
                  type="text"
                  name="confirmationCode"
                  value={forgotPasswordData.confirmationCode}
                  onChange={handleForgotPasswordChange}
                  placeholder="Enter the code from your email"
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>New Password</Form.Label>
                <Form.Control
                  type="password"
                  name="newPassword"
                  value={forgotPasswordData.newPassword}
                  onChange={handleForgotPasswordChange}
                  placeholder="Enter new password"
                  required
                />
                <Form.Text className="text-muted">
                  Must be at least 8 characters long
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>Confirm New Password</Form.Label>
                <Form.Control
                  type="password"
                  name="confirmNewPassword"
                  value={forgotPasswordData.confirmNewPassword}
                  onChange={handleForgotPasswordChange}
                  placeholder="Confirm new password"
                  required
                />
              </Form.Group>

              <div className="d-grid gap-2">
                <Button 
                  variant="primary" 
                  type="submit"
                  disabled={isForgotPasswordSubmitting}
                >
                  {isForgotPasswordSubmitting ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Resetting Password...
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </Button>
                <Button 
                  variant="secondary" 
                  onClick={handleForgotPasswordClose}
                  disabled={isForgotPasswordSubmitting}
                >
                  Cancel
                </Button>
              </div>
            </Form>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default UserProfile;